/**
 * GLSL for the Hillaire (2020) atmosphere. Every physical constant is emitted from ATMOSPHERE (atmosphere.ts),
 * so the CPU reference and the GPU cannot drift apart. Units: metres; radiance per unit sun illuminance.
 */
import { ATMOSPHERE as A } from './atmosphere';
import { CLOUD_GLSL } from './cloudShaders';

const f = (x: number): string => (Number.isInteger(x) ? `${x}.0` : `${x}`);
const v3 = (c: readonly number[]): string => `vec3(${c.map(f).join(', ')})`;

export const ATMOSPHERE_GLSL = /* glsl */ `
#define PI 3.14159265358979
const float Rg = ${f(A.groundRadiusM)};
const float Rt = ${f(A.topRadiusM)};
const vec3 RAYLEIGH = ${v3(A.rayleighScattering)};
const float HR = ${f(A.rayleighScaleHeightM)};
const float MIE_S = ${f(A.mieScattering)};
const float MIE_E = ${f(A.mieExtinction)};
const float HM = ${f(A.mieScaleHeightM)};
const float MIE_G = ${f(A.mieG)};
const vec3 OZONE = ${v3(A.ozoneAbsorption)};
const float OZ_C = ${f(A.ozoneCenterM)};
const float OZ_W = ${f(A.ozoneHalfWidthM)};

struct Medium { vec3 scatR; float scatM; vec3 ext; };

Medium mediumAt(float h) {
  Medium m;
  float r = exp(-h / HR);
  float mm = exp(-h / HM);
  float o = max(0.0, 1.0 - abs(h - OZ_C) / OZ_W);
  m.scatR = RAYLEIGH * r;
  m.scatM = MIE_S * mm;
  m.ext = RAYLEIGH * r + vec3(MIE_E * mm) + OZONE * o;
  return m;
}

/** Nearest positive distance from origin o (planet centre at 0) along unit d to a sphere of radius R, or -1. */
float raySphere(vec3 o, vec3 d, float R) {
  float b = dot(o, d);
  float c = dot(o, o) - R * R;
  float disc = b * b - c;
  if (disc < 0.0) return -1.0;
  float s = sqrt(disc);
  float t0 = -b - s;
  float t1 = -b + s;
  if (t0 > 0.0) return t0;
  if (t1 > 0.0) return t1;
  return -1.0;
}

float phaseRayleigh(float c) { return 3.0 / (16.0 * PI) * (1.0 + c * c); }
float phaseMie(float c) {
  float g = MIE_G;
  float k = 3.0 / (8.0 * PI) * (1.0 - g * g) / (2.0 + g * g);
  return k * (1.0 + c * c) / pow(max(1e-4, 1.0 + g * g - 2.0 * g * c), 1.5);
}

// Bruneton transmittance parameterisation.
vec2 transmittanceUv(float r, float mu) {
  float H = sqrt(Rt * Rt - Rg * Rg);
  float rho = sqrt(max(0.0, r * r - Rg * Rg));
  float disc = r * r * (mu * mu - 1.0) + Rt * Rt;
  float d = max(0.0, -r * mu + sqrt(max(disc, 0.0)));
  float dMin = Rt - r;
  float dMax = rho + H;
  return vec2((d - dMin) / max(1e-3, dMax - dMin), rho / H);
}

void transmittanceRMu(vec2 uv, out float r, out float mu) {
  float H = sqrt(Rt * Rt - Rg * Rg);
  float rho = H * uv.y;
  r = sqrt(rho * rho + Rg * Rg);
  float dMin = Rt - r;
  float dMax = rho + H;
  float d = dMin + uv.x * (dMax - dMin);
  mu = d == 0.0 ? 1.0 : clamp((H * H - rho * rho - d * d) / (2.0 * r * d), -1.0, 1.0);
}

vec3 sampleTransmittance(sampler2D lut, float r, float mu) {
  return texture2D(lut, transmittanceUv(r, mu)).rgb;
}

/** Transmittance toward a light from a point, including the planet's shadow. */
vec3 lightTransmittance(sampler2D lut, vec3 p, vec3 lightDir) {
  float r = length(p);
  vec3 up = p / r;
  if (raySphere(p, lightDir, Rg) > 0.0) return vec3(0.0);
  return sampleTransmittance(lut, r, dot(up, lightDir));
}

// Sky-view parameterisation (must match skyViewMapping.ts).
vec3 skyViewDir(vec2 uv, float camR) {
  float beta = acos(sqrt(max(0.0, camR * camR - Rg * Rg)) / camR);
  float zh = PI - beta;
  float zen;
  if (uv.y < 0.5) { float c = 1.0 - 2.0 * uv.y; zen = zh * (1.0 - c * c); }
  else { float c = 2.0 * uv.y - 1.0; zen = zh + beta * c * c; }
  float phi = uv.x * 2.0 * PI;
  float s = sin(zen);
  return vec3(s * sin(phi), cos(zen), -s * cos(phi));
}

vec2 skyViewUv(vec3 d, float camR) {
  float beta = acos(sqrt(max(0.0, camR * camR - Rg * Rg)) / camR);
  float zh = PI - beta;
  float zen = acos(clamp(d.y, -1.0, 1.0));
  float v = zen <= zh ? (1.0 - sqrt(max(0.0, 1.0 - zen / zh))) * 0.5 : 0.5 + sqrt(min(1.0, (zen - zh) / beta)) * 0.5;
  float u = atan(d.x, -d.z) / (2.0 * PI);
  return vec2(u < 0.0 ? u + 1.0 : u, v);
}
`;

export const FULLSCREEN_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

export const TRANSMITTANCE_FRAG = /* glsl */ `
${ATMOSPHERE_GLSL}
varying vec2 vUv;
void main() {
  float r, mu;
  transmittanceRMu(vUv, r, mu);
  vec3 o = vec3(0.0, r, 0.0);
  vec3 d = vec3(sqrt(max(0.0, 1.0 - mu * mu)), mu, 0.0);
  float len = raySphere(o, d, Rt);
  const int STEPS = 40;
  float dt = len / float(STEPS);
  vec3 tau = vec3(0.0);
  for (int i = 0; i < STEPS; i++) {
    vec3 p = o + d * (float(i) + 0.5) * dt;
    tau += mediumAt(length(p) - Rg).ext * dt;
  }
  gl_FragColor = vec4(exp(-tau), 1.0);
}
`;

export const MULTISCATTER_FRAG = /* glsl */ `
${ATMOSPHERE_GLSL}
uniform sampler2D uTransmittance;
varying vec2 vUv;
const float GROUND_ALBEDO = 0.3;
void main() {
  float cosSun = vUv.x * 2.0 - 1.0;
  float h = max(1.0, vUv.y * (Rt - Rg));
  vec3 o = vec3(0.0, Rg + h, 0.0);
  vec3 sun = vec3(sqrt(max(0.0, 1.0 - cosSun * cosSun)), cosSun, 0.0);
  const int DIRS = 64;
  const int STEPS = 20;
  vec3 L2 = vec3(0.0);
  vec3 fms = vec3(0.0);
  for (int k = 0; k < DIRS; k++) {
    float y = 1.0 - 2.0 * (float(k) + 0.5) / float(DIRS);
    float rr = sqrt(max(0.0, 1.0 - y * y));
    float ph = 2.39996323 * float(k);
    vec3 d = vec3(cos(ph) * rr, y, sin(ph) * rr);
    float tGround = raySphere(o, d, Rg);
    float tTop = raySphere(o, d, Rt);
    float len = tGround > 0.0 ? tGround : tTop;
    float dt = len / float(STEPS);
    vec3 thr = vec3(1.0);
    for (int i = 0; i < STEPS; i++) {
      vec3 p = o + d * (float(i) + 0.5) * dt;
      Medium m = mediumAt(length(p) - Rg);
      vec3 scat = m.scatR + vec3(m.scatM);
      vec3 stepT = exp(-m.ext * dt);
      vec3 integ = (vec3(1.0) - stepT) / max(m.ext, vec3(1e-9));
      vec3 sunT = lightTransmittance(uTransmittance, p, sun);
      L2 += thr * scat * sunT * (1.0 / (4.0 * PI)) * integ;
      fms += thr * scat * integ;
      thr *= stepT;
    }
    if (tGround > 0.0) {
      vec3 p = o + d * tGround;
      vec3 n = normalize(p);
      L2 += thr * lightTransmittance(uTransmittance, p + n * 1.0, sun) * max(0.0, dot(n, sun)) * GROUND_ALBEDO / PI;
    }
  }
  L2 /= float(DIRS);
  fms /= float(DIRS);
  gl_FragColor = vec4(L2 / max(vec3(1e-4), vec3(1.0) - fms), 1.0);
}
`;

/** Radiance of the sky seen from the camera, lit by one light (sun or moon), per unit light illuminance. */
export const SKYVIEW_FRAG = /* glsl */ `
${ATMOSPHERE_GLSL}
uniform sampler2D uTransmittance;
uniform sampler2D uMultiscatter;
uniform vec3 uLightDir;
uniform float uCamR;
varying vec2 vUv;
vec3 multiscatter(float r, float cosLight) {
  vec2 uv = vec2(clamp(cosLight * 0.5 + 0.5, 0.0, 1.0), clamp((r - Rg) / (Rt - Rg), 0.0, 1.0));
  return texture2D(uMultiscatter, uv).rgb;
}
void main() {
  vec3 d = skyViewDir(vUv, uCamR);
  vec3 o = vec3(0.0, uCamR, 0.0);
  float tGround = raySphere(o, d, Rg);
  float tTop = raySphere(o, d, Rt);
  float len = tGround > 0.0 ? tGround : tTop;
  len = min(len, 400000.0);
  const int STEPS = 32;
  vec3 L = vec3(0.0);
  vec3 thr = vec3(1.0);
  float c = dot(d, uLightDir);
  float pR = phaseRayleigh(c);
  float pM = phaseMie(c);
  float tPrev = 0.0;
  for (int i = 0; i < STEPS; i++) {
    // quadratic step distribution: dense near the camera where the air is thick
    float s = (float(i) + 1.0) / float(STEPS);
    float t = len * s * s;
    float dt = t - tPrev;
    vec3 p = o + d * (tPrev + 0.5 * dt);
    tPrev = t;
    float r = length(p);
    Medium m = mediumAt(r - Rg);
    vec3 up = p / r;
    vec3 lightT = lightTransmittance(uTransmittance, p, uLightDir);
    vec3 ms = multiscatter(r, dot(up, uLightDir));
    vec3 S = lightT * (m.scatR * pR + vec3(m.scatM * pM)) + ms * (m.scatR + vec3(m.scatM));
    vec3 stepT = exp(-m.ext * dt);
    L += thr * S * (vec3(1.0) - stepT) / max(m.ext, vec3(1e-9));
    thr *= stepT;
  }
  gl_FragColor = vec4(L, 1.0);
}
`;

export const SKY_DOME_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize((modelMatrix * vec4(position, 0.0)).xyz);
  vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_Position = p.xyww;
}
`;

/** Final sky: sun + moon sky-view LUTs, sun disc, airglow, town glow. HDR; tone mapping happens later. */
export const SKY_DOME_FRAG = /* glsl */ `
${ATMOSPHERE_GLSL}
uniform sampler2D uTransmittance;
uniform sampler2D uSkySun;
uniform sampler2D uSkyMoon;
uniform vec3 uSunDir;
uniform float uSunIlluminance;
uniform float uMoonIlluminance;
uniform float uCamR;
uniform vec3 uAirglow;
uniform vec3 uTownGlowColor;
uniform vec3 uTownGlowDir;
uniform float uTownGlow;
uniform vec3 uMoonDir;
varying vec3 vDir;
${CLOUD_GLSL}
void main() {
  vec3 d = normalize(vDir);
  vec2 uv = skyViewUv(d, uCamR);
  vec3 col = texture2D(uSkySun, uv).rgb * uSunIlluminance + texture2D(uSkyMoon, uv).rgb * uMoonIlluminance;
  float aboveHorizon = smoothstep(-0.02, 0.05, d.y);
  col += uAirglow * aboveHorizon;
  // warm light-pollution dome over the town, hugging the horizon
  float az = max(0.0, dot(normalize(vec3(d.x, 0.0, d.z)), uTownGlowDir));
  col += uTownGlowColor * uTownGlow * pow(az, 6.0) * exp(-max(0.0, d.y) * 14.0) * aboveHorizon;
  // sun disc with limb darkening, dimmed by the atmosphere toward the camera
  float cosA = dot(d, uSunDir);
  float disc = smoothstep(0.99998, 0.999992, cosA);
  if (disc > 0.0) {
    vec3 o = vec3(0.0, uCamR, 0.0);
    vec3 T = lightTransmittance(uTransmittance, o, uSunDir);
    float mu = clamp((cosA - 0.99998) / (1.0 - 0.99998), 0.0, 1.0);
    col += T * uSunIlluminance * 2400.0 * disc * (0.6 + 0.4 * sqrt(mu));
  }
  vec2 zen = skyViewUv(vec3(0.0, 1.0, 0.0), uCamR);
  vec3 ambient = texture2D(uSkySun, zen).rgb * uSunIlluminance + texture2D(uSkyMoon, zen).rgb * uMoonIlluminance + uAirglow;
  col = applyClouds(col, d, uCamR, uSunDir, uSunIlluminance, uMoonDir, uMoonIlluminance, ambient, uTransmittance);
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;
